import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import { Delete, Edit, Refresh } from '@mui/icons-material';
import notificationTemplatesService, { NotificationTemplate } from '../services/notificationTemplatesService';

type TemplateForm = {
  title: string;
  body: string;
  enabled: boolean;
  sortOrder: number;
};

const emptyForm: TemplateForm = {
  title: '',
  body: '',
  enabled: true,
  sortOrder: 0,
};

const NotificationTemplatesManager: React.FC = () => {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TemplateForm>(emptyForm);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleteBusy, setDeleteBusy] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await notificationTemplatesService.getTemplates();
      setTemplates(list);
    } catch (e: any) {
      setError(e?.message || 'Erreur chargement des modèles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sortOrder: templates.length > 0 ? (Math.max(...templates.map(t => t.sortOrder ?? 0)) + 1) : 0 });
    setDialogOpen(true);
    setSaved(null);
  };

  const openEdit = (t: NotificationTemplate) => {
    setEditingId(t.id);
    setForm({
      title: t.title || '',
      body: t.body || '',
      enabled: t.enabled === true,
      sortOrder: typeof t.sortOrder === 'number' ? t.sortOrder : 0,
    });
    setDialogOpen(true);
    setSaved(null);
  };

  const handleDelete = async (id: string) => {
    const ok = window.confirm('Supprimer ce modèle de notification ?');
    if (!ok) return;
    try {
      setDeleteBusy(id);
      setError(null);
      await notificationTemplatesService.deleteTemplate(id);
      setSaved('Modèle supprimé.');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur suppression du modèle');
    } finally {
      setDeleteBusy(null);
    }
  };

  const handleToggleEnabled = async (t: NotificationTemplate) => {
    try {
      setError(null);
      await notificationTemplatesService.updateTemplate(t.id, { enabled: !t.enabled });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur mise à jour statut');
    }
  };

  const handleSave = async () => {
    const title = form.title.trim();
    const body = form.body.trim();
    if (!title || !body) {
      setError('Titre et message requis.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSaved(null);

      if (editingId) {
        await notificationTemplatesService.updateTemplate(editingId, {
          title,
          body,
          enabled: form.enabled,
          sortOrder: form.sortOrder,
        });
        setSaved('Modèle mis à jour.');
      } else {
        await notificationTemplatesService.createTemplate({
          title,
          body,
          enabled: form.enabled,
          sortOrder: form.sortOrder,
        });
        setSaved('Modèle créé.');
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur enregistrement du modèle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Modèles de notifications</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Rafraîchir">
            <span>
              <IconButton onClick={() => void load()} disabled={loading}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
          <Button variant="contained" onClick={openCreate}>Nouveau modèle</Button>
        </Stack>
      </Box>

      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {saved && (
        <Box mb={2}>
          <Alert severity="success">{saved}</Alert>
        </Box>
      )}

      {loading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} /> <Typography>Chargement…</Typography>
        </Box>
      ) : templates.length === 0 ? (
        <Alert severity="info">Aucun modèle. Cliquez sur "Nouveau modèle" pour en créer.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ordre</TableCell>
              <TableCell>Titre</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Activé</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell width={90}>{t.sortOrder ?? 0}</TableCell>
                <TableCell width={220} sx={{ fontWeight: 600 }}>{t.title || '(Sans titre)'}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {t.body}
                  </Typography>
                </TableCell>
                <TableCell width={120}>
                  <FormControlLabel
                    control={<Switch checked={t.enabled} onChange={() => void handleToggleEnabled(t)} />}
                    label={t.enabled ? 'Oui' : 'Non'}
                  />
                </TableCell>
                <TableCell align="right" width={140}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Modifier">
                      <IconButton onClick={() => openEdit(t)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <span>
                        <IconButton color="error" onClick={() => void handleDelete(t.id)} disabled={deleteBusy === t.id}>
                          {deleteBusy === t.id ? <CircularProgress size={18} /> : <Delete />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Modifier le modèle' : 'Nouveau modèle'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Titre"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Message"
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              required
            />
            <TextField
              label="Ordre d'affichage"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 0 }))}
              fullWidth
              inputProps={{ step: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />
              }
              label="Activé"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Annuler</Button>
          <Button onClick={() => void handleSave()} variant="contained" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default NotificationTemplatesManager;
